import type { SxProps, Theme } from "@mui/material";
import { Drawer } from "@mui/material";
import classNames from "classnames";
import type { ReactNode } from "react";

import CollapseEdge from "./CollapseEdge";

interface NavigationLayoutProps {
    /** Navigation variant for styling */
    variant: "book" | "publisher" | "beneficiary";
    /** Whether on mobile device */
    isMobile: boolean;
    /** Whether the drawer is open (mobile only) */
    isOpen: boolean;
    /** Whether the navigation is collapsed (desktop only) */
    isCollapsed?: boolean;
    /** Whether in PWA mode */
    isPWAMode?: boolean;
    /** Top section content (header + main links) */
    topContent: ReactNode;
    /** Bottom section content (settings, profile, etc.) */
    bottomContent: ReactNode;
    /** Style to apply when content should be blocked */
    blockedStyle?: React.CSSProperties;
    /** Optional collapse button to render as floating */
    collapseButton?: ReactNode;
}

/**
 * Layout wrapper for navigation providing the Drawer structure
 * Centralizes the common Drawer configuration for all navigation variants
 * Supports collapsed state for desktop (icons only)
 */
const NavigationLayout = ({
    variant,
    isMobile,
    isOpen,
    isCollapsed = false,
    isPWAMode = false,
    topContent,
    bottomContent,
    blockedStyle,
    collapseButton,
}: NavigationLayoutProps) => {
    // Book variant is always desktop mode
    const isDesktop = variant === "book" || !isMobile;
    // Only allow collapse on desktop (not mobile, not beneficiary)
    const canCollapse = isDesktop && variant !== "beneficiary";
    const effectiveCollapsed = canCollapse && isCollapsed;

    // Mobile-only styles (desktop styles are in SCSS)
    const getMobileDrawerSx = (): SxProps<Theme> | undefined => {
        if (isDesktop) return undefined;

        return {
            "& .MuiDrawer-paper": {
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "space-between",
                gap: "34px",
                paddingBottom: isPWAMode ? "5rem" : "3rem",
                paddingTop: "1.5rem",
                backgroundColor: "#f6f6f6",
            },
        };
    };

    const wrapperClassName = classNames("navigation-wrapper", `navigation-wrapper--${variant}`, {
        "navigation-wrapper--collapsed": effectiveCollapsed,
    });

    return (
        <div className={wrapperClassName}>
            <Drawer
                anchor={isDesktop ? "left" : "bottom"}
                open={isDesktop ? true : isOpen}
                variant={isDesktop ? "permanent" : "temporary"}
                sx={getMobileDrawerSx()}
            >
                <div className="navigation-top" style={blockedStyle}>
                    {topContent}
                </div>
                <div className="navigation-bottom">{bottomContent}</div>
            </Drawer>
            {/* Edge hit area + floating collapse button */}
            {collapseButton && (
                <>
                    <CollapseEdge />
                    <div className="navigation-collapse-floating">{collapseButton}</div>
                </>
            )}
        </div>
    );
};

export default NavigationLayout;
